import {
  Control,
  Controller,
  ControllerProps,
  FieldError,
  Path
} from 'react-hook-form';
import type { CheckboxProps, FormControlLabelProps } from '@mui/material';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import Checkbox from '@mui/material/Checkbox';
import FormGroup from '@mui/material/FormGroup';
import { FieldValues } from 'react-hook-form/dist/types/fields';

/**
 * Componente CheckboxElement
 *
 * Campo de checkbox integrado con validaciones y react-hook-form.
 *
 * @component
 * @param {object} props - Props del componente.
 * @param {string} props.name - Nombre del campo en el formulario.
 * @param {object} [props.validation] - Reglas de validación.
 * @param {function} [props.parseError] - Función para parsear el error.
 * @param {string} [props.label] - Etiqueta del checkbox.
 * @param {string} [props.helperText] - Texto de ayuda o error.
 * @param {Control} [props.control] - Control de react-hook-form.
 * @example
 * <CheckboxElement name="acepto" control={control} label="Acepto los términos" />
 */

export type CheckboxElementProps<T> = Omit<CheckboxProps, 'name'> & {
  validation?: ControllerProps['rules'];
  name: Path<T>;
  parseError?: (error: FieldError) => string;
  label?: FormControlLabelProps['label'];
  helperText?: string;
  control?: Control<T>;
};

export function CheckboxElement<TFieldValues extends FieldValues>({
  name,
  validation = {},
  required,
  parseError,
  label,
  control,
  helperText,
  ...rest
}: CheckboxElementProps<TFieldValues>) {
  if (required && !validation.required) {
    validation.required = 'This field is required';
  }

  return (
    <Controller
      control={control}
      name={name}
      render={({
        field: { value, onChange },
        fieldState: { invalid, error }
      }) => {
        const parsedHelperText = error
          ? typeof parseError === 'function'
            ? parseError(error)
            : error.message
          : helperText;
        return (
          <FormControl error={invalid} required={required}>
            <FormGroup row>
              <FormControlLabel
                control={
                  <Checkbox
                    {...rest}
                    checked={!!value}
                    color={rest.color || 'primary'}
                    onChange={() => {
                      onChange(!value);
                    }}
                    sx={{
                      ...rest.sx,
                      width: '10px',
                      color: invalid ? 'error.main' : undefined
                    }}
                    value={value}
                  />
                }
                label={label || ''}
              />
            </FormGroup>
            {parsedHelperText && (
              <FormHelperText error={invalid}>
                {parsedHelperText}
              </FormHelperText>
            )}
          </FormControl>
        );
      }}
      rules={validation}
    />
  );
}

export default CheckboxElement;
